import { ApiError } from '@/api/client';

/**
 * The most useful sentence an API failure can offer a person.
 *
 * WHY THIS EXISTS. Two features had their own copy of this that read `error.response.data.errors`, the raw
 * Axios envelope — but `client.ts`'s interceptor has already unpacked that into an `ApiError` and
 * discarded `response`. So both branches missed, every mutation error rendered the literal string
 * "Something went wrong.", and the domain's carefully written sentence was thrown away.
 *
 * That was the bug behind "issuing a quotation that needs approval shows an error": the server did send
 * "QUO-2026-0001 is waiting on sign-off from Finance. It cannot be issued until that is given.", inside
 * `errors.status[0]`, and the UI replaced it with nothing.
 *
 * THE ORDER MATTERS AND IT IS NOT ARBITRARY. The API speaks RFC 9457, where `title` is always the generic
 * classification ("Validation failed") and `detail` the generic boilerplate ("The request contains invalid
 * data. See errors for details."). The only place the actionable text ever appears is inside `errors`, so
 * that is read first. Preferring `detail` — the obvious choice — yields boilerplate on every 422.
 */
export function apiErrorMessage(error: unknown, fallback = 'That could not be saved.'): string {
  if (!(error instanceof ApiError)) {
    return fallback;
  }

  // A network failure has no useful field errors and its `detail` IS the message.
  if (error.isNetworkError) {
    return error.detail ?? 'Could not reach the server. Check your connection and try again.';
  }

  const fieldMessages = Object.values(error.fieldErrors).flat().filter(Boolean);

  if (fieldMessages.length > 0) {
    // Joined rather than only the first: a request can fail two validations at once, and showing one
    // means the second arrives as a surprise on the next attempt.
    return fieldMessages.join(' ');
  }

  // 403s and 404s carry no `errors` bag, so `detail` is genuinely the best available text — and for those
  // statuses it is written rather than boilerplate.
  return error.detail ?? error.title ?? fallback;
}
